import { useState, useEffect, useMemo, useCallback } from 'react';
import { Mail, Save, Send, Search, Users, Eye, AlertCircle } from 'lucide-react';
import {
    trainingLogApi,
    ReminderConfig as ReminderConfigType,
    ReminderExclusion,
    ReminderPreview,
    ReminderRecipient,
} from '../../services/trainingLogApi';
import { isReminderTestAddress } from '../../utils/reminderTestAddress';

const EXCLUSION_LABELS: Record<ReminderExclusion, string> = {
    'private-address': 'Private address, not reminded',
    'contract-type': 'Contract type Hourly / Consultant',
    'opted-out': 'Opted out',
};

export function ReminderConfig() {
    const [config, setConfig] = useState<ReminderConfigType | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [savedConfig, setSavedConfig] = useState<Pick<ReminderConfigType, 'IsEnabled' | 'ReminderText' | 'AppUrl'> | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    // Recipients state
    const [recipients, setRecipients] = useState<ReminderRecipient[]>([]);
    const [recipientsBaseline, setRecipientsBaseline] = useState<Record<string, boolean>>({});
    const [recipientSearch, setRecipientSearch] = useState('');
    const [recipientsSaving, setRecipientsSaving] = useState(false);
    const [recipientsMsg, setRecipientsMsg] = useState<string | null>(null);
    const [recipientsError, setRecipientsError] = useState<string | null>(null);

    const [testSending, setTestSending] = useState(false);
    const [testTo, setTestTo] = useState('');

    const [preview, setPreview] = useState<ReminderPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const testAddressValid = isReminderTestAddress(testTo);

    const loadAll = useCallback(() => {
        setLoading(true);
        setLoadError(null);
        Promise.all([
            trainingLogApi.getReminderConfig(),
            trainingLogApi.getReminderRecipients(),
        ])
            .then(([cfg, recs]) => {
                setConfig(cfg);
                setSavedConfig({ IsEnabled: cfg.IsEnabled, ReminderText: cfg.ReminderText, AppUrl: cfg.AppUrl });
                setRecipients(recs);
                const baseline: Record<string, boolean> = {};
                recs.forEach(r => { baseline[r.Email.toLowerCase()] = r.IsEnabled; });
                setRecipientsBaseline(baseline);
            })
            .catch(err => setLoadError(err instanceof Error ? err.message : 'Request failed'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    // The test mail is built from the saved text (reminder-core reads cfg.ReminderText), so unsaved edits are not tested.
    const configDirty = config !== null && savedConfig !== null && (
        config.IsEnabled !== savedConfig.IsEnabled
        || config.ReminderText !== savedConfig.ReminderText
        || config.AppUrl !== savedConfig.AppUrl
    );

    const handleSave = async () => {
        if (!config) return;
        setSaving(true);
        setError(null);
        setSuccessMsg(null);

        try {
            await trainingLogApi.updateReminderConfig({
                IsEnabled: config.IsEnabled,
                ReminderText: config.ReminderText,
                AppUrl: config.AppUrl,
            });
            setSavedConfig({ IsEnabled: config.IsEnabled, ReminderText: config.ReminderText, AppUrl: config.AppUrl });
            setSuccessMsg('Configuration saved successfully');
            setTimeout(() => setSuccessMsg(null), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const toggleRecipient = (email: string, enabled: boolean) => {
        setRecipients(prev => prev.map(r =>
            r.Email.toLowerCase() === email.toLowerCase() ? { ...r, IsEnabled: enabled } : r
        ));
    };

    const setAllRecipients = (enabled: boolean) => {
        // Private-address rows are placeholders; the server refuses them, so bulk toggles skip them.
        setRecipients(prev => prev.map(r =>
            r.ExcludedReason === 'private-address' ? r : { ...r, IsEnabled: enabled }
        ));
    };

    const recipientsDirty = useMemo(() => {
        return recipients.some(r => recipientsBaseline[r.Email.toLowerCase()] !== r.IsEnabled);
    }, [recipients, recipientsBaseline]);

    const filteredRecipients = useMemo(() => {
        const q = recipientSearch.trim().toLowerCase();
        if (!q) return recipients;
        return recipients.filter(r => {
            const name = `${r.FirstName ?? ''} ${r.LastName ?? ''}`.toLowerCase();
            return name.includes(q)
                || (r.Email?.toLowerCase().includes(q) ?? false)
                || (r.Department?.toLowerCase().includes(q) ?? false);
        });
    }, [recipients, recipientSearch]);

    const enabledCount = recipients.filter(r =>
        r.IsEnabled && r.ExcludedReason !== 'private-address' && r.ExcludedReason !== 'contract-type'
    ).length;

    const handleSaveRecipients = async () => {
        setRecipientsSaving(true);
        setRecipientsError(null);
        setRecipientsMsg(null);

        try {
            const changed = recipients.filter(r => recipientsBaseline[r.Email.toLowerCase()] !== r.IsEnabled);
            await trainingLogApi.updateReminderRecipients(
                changed.map(r => ({ email: r.Email, isEnabled: r.IsEnabled }))
            );
            const newBaseline: Record<string, boolean> = {};
            recipients.forEach(r => { newBaseline[r.Email.toLowerCase()] = r.IsEnabled; });
            setRecipientsBaseline(newBaseline);
            setRecipientsMsg(`Saved ${changed.length} change${changed.length === 1 ? '' : 's'}.`);
            setTimeout(() => setRecipientsMsg(null), 3000);
        } catch (err) {
            setRecipientsError(err instanceof Error ? err.message : 'Failed to save recipients');
        } finally {
            setRecipientsSaving(false);
        }
    };

    const handleTestSend = async () => {
        setTestSending(true);
        setError(null);
        setSuccessMsg(null);
        try {
            // The endpoint answers 200 even when the mail did not go out: read sent/failed/error.
            const result = await trainingLogApi.sendTestReminder(testTo.trim());
            if (result.sent === 1) {
                setSuccessMsg(`Test reminder sent to ${result.sentTo}.`);
                setTimeout(() => setSuccessMsg(null), 4000);
            } else {
                setError(`Test reminder not sent to ${result.sentTo} (sent ${result.sent}, failed ${result.failed})${result.error ? `: ${result.error}` : '.'}`);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to send test reminder');
        } finally {
            setTestSending(false);
        }
    };

    const handlePreview = async () => {
        setPreviewLoading(true);
        setPreviewError(null);
        try {
            setPreview(await trainingLogApi.getReminderPreview());
        } catch (err) {
            setPreview(null);
            setPreviewError(err instanceof Error ? err.message : 'Failed to load preview');
        } finally {
            setPreviewLoading(false);
        }
    };

    if (loading) return <div className="text-sm text-gray-500">Loading config...</div>;
    if (!config) {
        return (
            <div role="alert" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="flex-1">
                    <div className="font-medium">The reminder settings could not be loaded.</div>
                    <div className="text-red-700">{loadError ?? 'No configuration was returned.'}</div>
                </div>
                <button type="button" onClick={loadAll} className="px-3 py-1 border border-red-300 rounded-md bg-white text-red-800 hover:bg-red-100">
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Reminder configuration */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Mail className="w-5 h-5 text-gray-600" />
                    <h3 className="text-base font-semibold text-gray-900">Email Reminder Configuration</h3>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded border border-red-200 mb-4">
                        {error}
                    </div>
                )}
                {successMsg && (
                    <div className="bg-green-50 text-green-700 text-sm px-3 py-2 rounded border border-green-200 mb-4">
                        {successMsg}
                    </div>
                )}

                <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={config.IsEnabled}
                            onChange={e => setConfig({ ...config, IsEnabled: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                        />
                        <span className="text-sm font-medium text-gray-700">
                            Enable monthly reminder emails
                        </span>
                    </label>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <div className="block text-sm font-medium text-gray-700 mb-1">
                                Send on
                            </div>
                            <div className="text-sm text-gray-900 py-2">
                                Last working day of the month
                            </div>
                            <div className="text-xs text-gray-500">
                                Monday to Friday, Zurich. Public holidays count as working days.
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                App URL (included in email)
                            </label>
                            <input
                                type="text"
                                value={config.AppUrl}
                                onChange={e => setConfig({ ...config, AppUrl: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="https://..."
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reminder Text
                        </label>
                        <textarea
                            value={config.ReminderText}
                            onChange={e => setConfig({ ...config, ReminderText: e.target.value })}
                            rows={4}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                            Placeholders: <code className="px-1 bg-gray-100 rounded">{'{FirstName}'}</code>,{' '}
                            <code className="px-1 bg-gray-100 rounded">{'{Month}'}</code>,{' '}
                            <code className="px-1 bg-gray-100 rounded">{'{AppUrl}'}</code>,{' '}
                            <code className="px-1 bg-gray-100 rounded">{'{ConfirmUrl}'}</code>
                        </div>
                    </div>

                    <div className="flex flex-wrap justify-between items-end gap-3 pt-2">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="reminder-test-to">
                                Test address
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    id="reminder-test-to"
                                    type="email"
                                    value={testTo}
                                    onChange={e => setTestTo(e.target.value)}
                                    placeholder="name@dolder.com"
                                    className="w-64 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <button
                                    onClick={handleTestSend}
                                    disabled={testSending || !testAddressValid || configDirty || saving}
                                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                                    title={configDirty
                                        ? 'Save first - the test uses the saved text'
                                        : 'Send exactly one test mail (subject starts with [TEST])'}
                                >
                                    <Send className="w-4 h-4" />
                                    {testSending ? 'Sending...' : 'Send test reminder'}
                                </button>
                            </div>
                            <div className={`text-xs mt-1 ${testTo.trim() && !testAddressValid ? 'text-red-600' : 'text-gray-500'}`}>
                                Only @dolder.com or @dolderbigler.com addresses.
                            </div>
                            {configDirty && (
                                <div className="text-xs mt-1 text-amber-700">
                                    Save first - the test uses the saved text.
                                </div>
                            )}
                        </div>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save Configuration'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Preview: who would get the reminder (dry run, sends nothing) */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Eye className="w-5 h-5 text-gray-600" />
                        <h3 className="text-base font-semibold text-gray-900">Preview</h3>
                    </div>
                    <button
                        onClick={handlePreview}
                        disabled={previewLoading}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                        {previewLoading ? 'Loading...' : 'Show recipients for this month'}
                    </button>
                </div>
                {previewError && (
                    <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded border border-red-200 mb-3">
                        {previewError}
                    </div>
                )}
                {preview && (
                    <div className="space-y-2">
                        <div className="text-sm text-gray-700">
                            <strong>{preview.count}</strong> recipient{preview.count === 1 ? '' : 's'} for {preview.monthLabel}
                            {preview.alreadySent > 0 && <>, {preview.alreadySent} already reminded</>}.
                            {' '}Reminder day: {preview.reminderDay}.
                            {preview.enabled !== true && (
                                <span className="text-amber-700"> Reminders are switched off, nothing is sent.</span>
                            )}
                        </div>
                        <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
                            {preview.recipients.length === 0 ? (
                                <div className="p-4 text-sm text-gray-500 text-center">Nobody would be reminded.</div>
                            ) : preview.recipients.map(p => (
                                <div key={p.email} className="flex items-center justify-between gap-3 px-3 py-1.5 border-b border-gray-100 last:border-b-0 text-sm">
                                    <span className="truncate text-gray-900">{p.firstName} <span className="text-xs text-gray-500">{p.email}</span></span>
                                    <span className={`text-xs ${p.status === 'already-sent' ? 'text-gray-500' : 'text-green-700'}`}>
                                        {p.status === 'already-sent' ? 'already sent' : 'would send'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Reminder recipients */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-gray-600" />
                        <h3 className="text-base font-semibold text-gray-900">Reminder Recipients</h3>
                        <span className="text-xs text-gray-500 ml-1">
                            ({enabledCount} of {recipients.length} active)
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setAllRecipients(true)}
                            className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                        >
                            Enable all
                        </button>
                        <button
                            onClick={() => setAllRecipients(false)}
                            className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                        >
                            Disable all
                        </button>
                    </div>
                </div>

                {recipientsError && (
                    <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded border border-red-200 mb-3">
                        {recipientsError}
                    </div>
                )}
                {recipientsMsg && (
                    <div className="bg-green-50 text-green-700 text-sm px-3 py-2 rounded border border-green-200 mb-3">
                        {recipientsMsg}
                    </div>
                )}

                <div className="relative mb-3">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        value={recipientSearch}
                        onChange={e => setRecipientSearch(e.target.value)}
                        placeholder="Search by name, email or department..."
                        className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto">
                    {filteredRecipients.length === 0 ? (
                        <div className="p-4 text-sm text-gray-500 text-center">
                            {recipientSearch ? 'No matches.' : 'No active employees found.'}
                        </div>
                    ) : (
                        filteredRecipients.map(r => (
                            <label
                                key={r.Email}
                                className="flex items-center gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={r.IsEnabled}
                                    onChange={e => toggleRecipient(r.Email, e.target.checked)}
                                    disabled={r.ExcludedReason === 'private-address'}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-gray-900 truncate">
                                        {r.FirstName} {r.LastName}
                                        {r.Department && (
                                            <span className="text-gray-500 ml-2 text-xs">– {r.Department}</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">{r.Email}</div>
                                </div>
                                {r.ExcludedReason && (
                                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap">
                                        {EXCLUSION_LABELS[r.ExcludedReason]}
                                    </span>
                                )}
                            </label>
                        ))
                    )}
                </div>

                <div className="flex justify-end mt-3">
                    <button
                        onClick={handleSaveRecipients}
                        disabled={!recipientsDirty || recipientsSaving}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        <Save className="w-4 h-4" />
                        {recipientsSaving ? 'Saving...' : 'Save Recipients'}
                    </button>
                </div>
            </div>
        </div>
    );
}
